import { useEffect, useState } from "react";
import LegalReadinessWidget from "../components/client-legal/LegalReadinessWidget";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "@/i18n/I18nProvider";
import { StatusBadge, RiskPill, fmtDate, fmtCurrency } from "../lib/status";
import BusinessReadinessCard from "../components/BusinessReadinessCard";
import BookingIntakeFunnel from "../components/booking_intake/IntakeFunnelWidget";
import DashboardShortcutCard from "../components/DashboardShortcutCard";
import {
  CalendarClock, ClipboardCheck, AlertTriangle, Car, Inbox, Plus, ArrowRight,
  AlertOctagon, BellRing, Crown, Receipt, Truck, RotateCcw, MessageSquareWarning,
  CheckCircle2, HelpCircle, X, ShieldAlert, Wrench, Users, Clock,
} from "lucide-react";

// Tier 1: "What needs my attention?": the same urgency-ordered feed as
// before, extended with the new Phase 2J signals (overdue returns, Needs
// Inspection, unresolved deposits, critical warnings). Ordered roughly by
// urgency; this is operational triage, not a science project.
function buildAttention(c) {
  const items = [];
  if (c.safety_holds_active > 0)
    items.push({ key: "safety_holds", level: "red",
                  title: `${c.safety_holds_active} vehicle${c.safety_holds_active === 1 ? "" : "s"} under Safety Hold`,
                  hint: "Cannot complete a ConfirmPH handover until resolved",
                  to: "/app/vehicles", testId: "att-safety-holds" });
  if (c.affected_bookings > 0)
    items.push({ key: "affected",   level: "red",
                  title: `${c.affected_bookings} booking${c.affected_bookings === 1 ? "" : "s"} affected by a problem`,
                  to: "/app/incidents", testId: "att-affected" });
  if (c.at_risk > 0)
    items.push({ key: "at_risk",    level: "red",
                  title: `${c.at_risk} at-risk booking${c.at_risk === 1 ? "" : "s"}`,
                  hint: "Conflicts or buffer warnings detected",
                  to: "/app/bookings?status=at_risk", testId: "att-at-risk" });
  if (c.overdue_returns > 0)
    items.push({ key: "overdue_returns", level: "red",
                  title: `${c.overdue_returns} overdue return${c.overdue_returns === 1 ? "" : "s"}`,
                  hint: "Scheduled return time has passed",
                  to: "/app/warnings", testId: "att-overdue-returns" });
  if (c.needs_inspection > 0)
    items.push({ key: "needs_inspection", level: "red",
                  title: `${c.needs_inspection} vehicle${c.needs_inspection === 1 ? "" : "s"} need${c.needs_inspection === 1 ? "s" : ""} inspection`,
                  to: "/app/vehicles", testId: "att-needs-inspection" });
  if (c.deposits_unresolved > 0)
    items.push({ key: "deposits_unresolved", level: "red",
                  title: `${c.deposits_unresolved} unresolved deposit${c.deposits_unresolved === 1 ? "" : "s"}`,
                  to: "/app/warnings", testId: "att-deposits-unresolved" });
  // Exception-flow gap closure items 2-3: the booking/request itself is a
  // real, unaffected state in both cases - only the notification delivery
  // (item 2) or the pending customer decision (item 3) needs attention.
  if (c.confirmation_deliveries_failed > 0)
    items.push({ key: "confirmation_deliveries_failed", level: "amber",
                  title: `${c.confirmation_deliveries_failed} confirmation${c.confirmation_deliveries_failed === 1 ? "" : "s"} failed to deliver`,
                  hint: "The booking itself is fine - the customer may not have seen it yet",
                  to: "#failed-deliveries-panel", testId: "att-confirmation-deliveries-failed" });
  if (c.extensions_awaiting_confirmation > 0)
    items.push({ key: "extensions_awaiting_confirmation", level: "amber",
                  title: `${c.extensions_awaiting_confirmation} extension${c.extensions_awaiting_confirmation === 1 ? "" : "s"} awaiting customer confirmation`,
                  to: "#pending-extensions-panel", testId: "att-extensions-awaiting-confirmation" });
  if (c.driver_required_unassigned > 0)
    items.push({ key: "driver_unassigned", level: "red",
                  title: `${c.driver_required_unassigned} booking${c.driver_required_unassigned === 1 ? "" : "s"} need${c.driver_required_unassigned === 1 ? "s" : ""} a driver assigned`,
                  to: "/app/bookings", testId: "att-driver-unassigned" });
  if (c.driver_unavailable_upcoming > 0)
    items.push({ key: "driver_unavailable", level: "red",
                  title: `${c.driver_unavailable_upcoming} upcoming booking${c.driver_unavailable_upcoming === 1 ? "" : "s"} with a conflicted or unavailable driver`,
                  hint: "Assigned driver is suspended, unavailable, or on leave during the booking window",
                  to: "/app/drivers", testId: "att-driver-unavailable" });
  if (c.reminders_critical_pending > 0)
    items.push({ key: "critical",   level: "red",
                  title: `${c.reminders_critical_pending} critical follow-up${c.reminders_critical_pending === 1 ? "" : "s"}`,
                  to: "/app/reminders?status=pending&priority=critical", testId: "att-critical-reminders" });
  if (c.warnings_critical > 0)
    items.push({ key: "warnings_critical", level: "red",
                  title: `${c.warnings_critical} critical warning${c.warnings_critical === 1 ? "" : "s"}`,
                  to: "/app/warnings", testId: "att-warnings-critical" });
  if (c.change_requested > 0)
    items.push({ key: "change",     level: "amber",
                  title: `${c.change_requested} customer correction request${c.change_requested === 1 ? "" : "s"}`,
                  to: "/app/bookings?change_requested=true", testId: "att-change-req" });
  if (c.verification_requests_new > 0)
    items.push({ key: "verif",      level: "amber",
                  title: `${c.verification_requests_new} new customer request${c.verification_requests_new === 1 ? "" : "s"}`,
                  to: "/app/requests", testId: "att-customer-requests" });
  if (c.inquiries_new > 0)
    items.push({ key: "inquiries",  level: "amber",
                  title: `${c.inquiries_new} new customer inquir${c.inquiries_new === 1 ? "y" : "ies"} from Discover Rentals`,
                  to: "/app/inquiries", testId: "att-inquiries-new" });
  if (c.reminders_due > 0)
    items.push({ key: "rem_due",    level: "amber",
                  title: `${c.reminders_due} follow-up${c.reminders_due === 1 ? "" : "s"} due now`,
                  to: "/app/reminders?status=pending", testId: "att-reminders-due" });
  if (c.driver_license_expiring > 0)
    items.push({ key: "driver_license", level: "amber",
                  title: `${c.driver_license_expiring} driver license${c.driver_license_expiring === 1 ? "" : "s"} expiring within 30 days`,
                  to: "/app/drivers", testId: "att-driver-license" });
  if (c.driver_documents_expiring > 0)
    items.push({ key: "driver_docs", level: "amber",
                  title: `${c.driver_documents_expiring} driver document${c.driver_documents_expiring === 1 ? "" : "s"} expiring within 30 days`,
                  to: "/app/drivers", testId: "att-driver-docs" });
  if (c.driver_reminders_overdue > 0)
    items.push({ key: "driver_reminders", level: "amber",
                  title: `${c.driver_reminders_overdue} overdue driver reminder${c.driver_reminders_overdue === 1 ? "" : "s"}`,
                  to: "/app/reminders?status=pending", testId: "att-driver-reminders" });
  if (c.pending > 0)
    items.push({ key: "pending",    level: "amber",
                  title: `${c.pending} booking${c.pending === 1 ? "" : "s"} awaiting customer confirmation`,
                  to: "/app/bookings?status=awaiting_customer_confirmation", testId: "att-pending" });
  if (c.open_incidents > 0)
    items.push({ key: "incidents",  level: "amber",
                  title: `${c.open_incidents} open problem${c.open_incidents === 1 ? "" : "s"}`,
                  to: "/app/incidents", testId: "att-open-incidents" });
  return items.slice(0, 9);
}

// Exception-flow gap closure items 2-3: click-through detail lists for the
// two new attention items above. Each row links straight to the booking,
// where the existing resend/correct-contact/copy-link/staff-assignment
// actions already live - nothing new is duplicated here.
function AttentionListPanel({ id, title, rows, kind }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div id={id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-5" data-testid={id}>
      <h3 className="font-display text-base font-semibold text-amber-900">{title}</h3>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <Link key={r.booking_id} to={`/app/bookings/${r.booking_id}`}
                className="block rounded-lg border border-amber-100 bg-white p-3 hover:bg-amber-50 text-sm">
            {kind === "failed-deliveries" ? (
              <>
                <div className="font-medium text-slate-900">{r.customer_name || "—"} · {r.booking_reference}</div>
                <div className="text-slate-600 text-xs mt-0.5">
                  {r.vehicle_name || "—"} · {r.channel || "—"} ·{" "}
                  {r.outcome === "provider_timeout" ? "Delivery timed out" : "Delivery failed"}
                  {r.attempts > 1 ? ` (${r.attempts} attempts)` : ""}
                </div>
                {r.error_message && <div className="text-xs text-slate-500 mt-1">{r.error_message}</div>}
              </>
            ) : (
              <>
                <div className="font-medium text-slate-900">{r.customer_name || "—"} · {r.booking_reference}</div>
                <div className="text-slate-600 text-xs mt-0.5">
                  {r.vehicle_name || "—"} · original return {fmtDate(r.original_end_datetime)} → proposed {fmtDate(r.pending_extension_end_datetime)}
                </div>
                {r.requested_at && <div className="text-xs text-slate-500 mt-1">Sent {fmtDate(r.requested_at)}</div>}
              </>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHow, setShowHow] = useState(false);
  const [inAppSummary, setInAppSummary] = useState(null);
  const [failedDeliveries, setFailedDeliveries] = useState(null);
  const [pendingExtensions, setPendingExtensions] = useState(null);

  useEffect(() => {
    const calls = [api.get("/dashboard/stats")];
    if (isOwner) calls.push(api.get("/billing/status"));
    Promise.all(calls).then(([s, b]) => {
      setData(s.data);
      if (b) setBilling(b.data);
      // Only fetch the detail list when the count says there's something to
      // show - keeps the normal (clean) dashboard load to one call.
      if (s.data?.counts?.confirmation_deliveries_failed > 0) {
        api.get("/dashboard/attention/failed-deliveries")
          .then((r) => setFailedDeliveries(r.data || [])).catch(() => setFailedDeliveries([]));
      }
      if (s.data?.counts?.extensions_awaiting_confirmation > 0) {
        api.get("/dashboard/attention/pending-extensions")
          .then((r) => setPendingExtensions(r.data || [])).catch(() => setPendingExtensions([]));
      }
    }).finally(() => setLoading(false));
    // Best-effort in-app bookings summary; silently ignored if Basic plan
    // or feature off (the endpoint always returns 200, just zeros).
    api.get("/in-app-bookings/dashboard-summary")
      .then((r) => setInAppSummary(r.data))
      .catch(() => {});
    // Tier 4: Staff on Duty strip. Best-effort; hidden if no permission.
    api.get("/staff/availability")
      .then((r) => setStaff(r.data))
      .catch(() => {});
  }, [isOwner]);

  // While the dashboard is mounted, advertise the Dashboard PWA manifest
  // so an "Add to home screen" from here installs a shortcut that opens
  // /app directly (not the public homepage). Restored on unmount.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute("href");
    if (original && original.startsWith("/manifest-dashboard.json")) return; // already set
    link.setAttribute("href", "/manifest-dashboard.json?v=identity3");
    if (typeof window !== "undefined") window.__confirmphInstallEvent = null;
    return () => {
      if (original) link.setAttribute("href", original);
    };
  }, []);

  if (loading) return <div className="text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;
  if (!data) return null;
  const c = data.counts;
  const todayPickups = c.today_pickups ?? 0;
  const todayReturns = c.today_returns ?? 0;
  const attention = buildAttention(c);
  const snapshot = data.fleet_snapshot || {};
  const gettingStarted = data.getting_started || {};

  // Tier 2: "Today". The daily, repeatable actions.
  const workspace = [
    { label: t("dash.ws.today_pickups"),     value: todayPickups,           icon: Truck,             tone: "blue",     testId: "ws-today-pickups",     to: "/app/bookings?today=pickups" },
    { label: t("dash.ws.today_returns"),     value: todayReturns,           icon: RotateCcw,         tone: "indigo",   testId: "ws-today-returns",     to: "/app/bookings?today=returns" },
    { label: t("dash.ws.needs_confirmation"),value: c.pending,              icon: ClipboardCheck,    tone: "amber",    testId: "ws-needs-confirmation", to: "/app/bookings?status=awaiting_customer_confirmation" },
    { label: "Reconfirmation Required",      value: c.reconfirmation_required ?? 0, icon: ShieldAlert, tone: (c.reconfirmation_required ?? 0) > 0 ? "amber" : "slate", testId: "ws-reconfirmation", to: "/app/bookings" },
    { label: t("dash.ws.problems"),          value: c.open_incidents,       icon: AlertOctagon,      tone: c.open_incidents > 0 ? "red" : "slate", testId: "ws-problems",   to: "/app/incidents" },
    { label: t("dash.ws.followups"),         value: c.reminders_due ?? 0,   icon: BellRing,          tone: (c.reminders_due ?? 0) > 0 ? "amber" : "slate", testId: "ws-followups",   to: "/app/reminders?status=pending" },
    { label: t("dash.ws.customer_requests"), value: c.verification_requests_new, icon: Inbox,         tone: c.verification_requests_new > 0 ? "amber" : "slate", testId: "ws-customer-requests", to: "/app/requests" },
  ];
  // With-driver pickups today: only a meaningful tile once a business has
  // any driver-service activity; naturally absent (0) for self-drive-only
  // businesses, so no separate driver_services_enabled check is needed here.
  if ((c.driver_pickups_today ?? 0) > 0) {
    workspace.push({ label: "Driver pickups today", value: c.driver_pickups_today, icon: Users,
                      tone: "blue", testId: "ws-driver-pickups", to: "/app/drivers" });
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t("dash.title")}</h1>
          <p className="text-slate-600 mt-1 inline-flex items-center gap-2">
            {t("dash.subtitle")}{" "}
            <button data-testid="dashboard-how-it-works" onClick={() => setShowHow(true)}
                    className="inline-flex items-center gap-1 text-[#1B4332] underline underline-offset-4 hover:no-underline">
              <HelpCircle className="w-3.5 h-3.5" /> {t("dash.how_it_works")}
            </button>
          </p>
        </div>
        <Link
          to="/app/bookings/new"
          data-testid="dashboard-new-booking-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium text-base"
        >
          <Plus className="w-5 h-5" /> {t("btn.add_booking")}
        </Link>
      </div>

      <LegalReadinessWidget />

      {/* Tier 1: Requires attention */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="attention-feed">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold">{t("dash.attention.title")}</h2>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">{t("dash.attention.subtitle")}</p>
          </div>
          <Link to="/app/bookings" className="hidden sm:inline-flex text-sm text-[#1B4332] font-medium hover:underline items-center gap-1">
            {t("btn.view_all")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <ul className="mt-4 divide-y divide-slate-100">
          {attention.length === 0 && (
            <li className="py-8 text-center text-sm text-slate-500" data-testid="attention-empty">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              {t("dash.attention.empty")}
            </li>
          )}
          {attention.map((it) => (
            <li key={it.key} data-testid={it.testId}>
              <Link to={it.to} className="flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded-lg hover:bg-slate-50">
                <div className="flex items-start gap-3 min-w-0">
                  <span className={`mt-0.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                    it.level === "red" ? "bg-red-500" : "bg-amber-500"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{it.title}</div>
                    {it.hint && <div className="text-xs text-slate-500 mt-0.5">{it.hint}</div>}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <AttentionListPanel id="failed-deliveries-panel" kind="failed-deliveries"
                           title="Confirmation Delivery Failed" rows={failedDeliveries} />
      <AttentionListPanel id="pending-extensions-panel" kind="pending-extensions"
                           title="Extensions Awaiting Confirmation" rows={pendingExtensions} />

      {/* Tier 2: Today */}
      <div>
        <h2 className="font-display text-lg font-semibold mb-3">Today</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {workspace.map((w) => <WorkCard key={w.label} {...w} />)}
        </div>
      </div>

      {/* Tier 3: Fleet snapshot */}
      <div>
        <h2 className="font-display text-lg font-semibold mb-3">Fleet snapshot</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fleet-snapshot">
          <SnapshotTile label="Available" value={snapshot.available} tone="green" icon={Car} testId="snap-available" />
          <SnapshotTile label="In Use" value={snapshot.in_use} tone="blue" icon={Truck} testId="snap-in-use" />
          <SnapshotTile label="Maintenance" value={snapshot.maintenance} tone="amber" icon={Wrench} testId="snap-maintenance" />
          <SnapshotTile label="Needs Inspection" value={snapshot.needs_inspection} tone="red" icon={AlertTriangle} testId="snap-needs-inspection" />
        </div>
      </div>

      {/* Tier 4: Staff on Duty */}
      {staff && (
        <Link to="/app/staff" data-testid="staff-on-duty-strip"
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-300 hover:shadow-sm transition">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
              <Users className="w-4.5 h-4.5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {staff.online_count ?? 0} staff on duty
              </div>
              <div className="text-xs text-slate-500 truncate">
                {(staff.online || []).slice(0, 4).map((p) => p.name).join(", ") || "No one clocked in right now"}
              </div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </Link>
      )}

      {/* Getting Started activation funnel: persistent until all 4 steps done */}
      <GettingStartedCard gettingStarted={gettingStarted} />

      <BusinessReadinessCard />

      {/* Owner-only billing banner */}
      {isOwner && billing && <BillingBanner billing={billing} />}

      {/* In-app bookings widget (silent on Basic / disabled feature) */}
      <BookingIntakeFunnel />

      {/* Home-screen shortcut prompt: dismissible per device */}
      <DashboardShortcutCard />
      {inAppSummary && (inAppSummary.pending_requests > 0 || inAppSummary.active_holds > 0) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="dashboard-in-app-widget">          <BellRing className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">In-app booking activity</div>
            <p className="text-sm text-amber-900 mt-0.5">
              <Link to="/app/bookings?source=public_booking_request" className="underline hover:no-underline font-medium">
                {inAppSummary.pending_requests} pending request{inAppSummary.pending_requests === 1 ? "" : "s"}
              </Link>
              {" · "}
              <Link to="/app/bookings?is_hold=true" className="underline hover:no-underline font-medium">
                {inAppSummary.active_holds} active hold{inAppSummary.active_holds === 1 ? "" : "s"}
              </Link>
              {inAppSummary.expiring_soon > 0 && (
                <> · <strong>{inAppSummary.expiring_soon} expiring within 15 minutes</strong></>
              )}
            </p>
          </div>
        </div>
      )}

      <DailyChecklist counts={c} />

      {/* Upcoming + At Risk */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel
          title={t("dash.upcoming.title")}
          subtitle={t("dash.upcoming.subtitle")}
          empty={t("dash.upcoming.empty")}
          link={{ to: "/app/bookings", label: t("nav.bookings") }}
          items={data.upcoming}
          testId="upcoming-panel"
        />
        <Panel
          title={t("dash.at_risk.title")}
          subtitle={t("dash.at_risk.subtitle")}
          empty={t("dash.at_risk.empty")}
          link={{ to: "/app/bookings", label: t("nav.bookings") }}
          items={data.at_risk_bookings}
          accent="risk"
          testId="at-risk-panel"
        />
      </div>

      {showHow && <HowItWorksModal onClose={() => setShowHow(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function WorkCard({ label, value, icon: Icon, tone, testId, to }) {
  const tones = {
    blue:    "bg-blue-50    border-blue-100    text-blue-900    hover:bg-blue-100/60",
    indigo:  "bg-indigo-50  border-indigo-100  text-indigo-900  hover:bg-indigo-100/60",
    amber:   "bg-amber-50   border-amber-100   text-amber-900   hover:bg-amber-100/60",
    red:     "bg-rose-50    border-rose-100    text-rose-900    hover:bg-rose-100/60",
    slate:   "bg-slate-50   border-slate-200   text-slate-700   hover:bg-slate-100",
    green:   "bg-emerald-50 border-emerald-100 text-emerald-900 hover:bg-emerald-100/60",
  };
  return (
    <Link to={to} data-testid={testId}
          className={`rounded-xl border p-4 transition-colors ${tones[tone] || tones.slate}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-80">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-2 text-3xl font-display font-semibold">{value ?? 0}</div>
    </Link>
  );
}

function SnapshotTile({ label, value, icon: Icon, tone, testId }) {
  const tones = {
    green: "bg-emerald-50 border-emerald-100 text-emerald-900",
    blue:  "bg-blue-50    border-blue-100    text-blue-900",
    amber: "bg-amber-50   border-amber-100   text-amber-900",
    red:   "bg-rose-50    border-rose-100    text-rose-900",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.blue}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-80">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-display font-semibold">{value ?? 0}</div>
    </div>
  );
}

function DailyChecklist({ counts: c }) {
  const { t } = useT();
  const items = [
    { label: t("dash.chk.pending"),           done: c.pending === 0,                 to: "/app/bookings?status=awaiting_customer_confirmation", testId: "chk-pending" },
    { label: t("dash.chk.today"),             done: (c.today_pickups ?? 0) === 0,    to: "/app/bookings?today=pickups",                       testId: "chk-today" },
    { label: t("dash.chk.problems"),          done: c.open_incidents === 0,          to: "/app/incidents",                                     testId: "chk-problems" },
    { label: t("dash.chk.followups"),         done: (c.reminders_due ?? 0) === 0,    to: "/app/reminders?status=pending",                      testId: "chk-followups" },
    { label: t("dash.chk.customer_requests"), done: c.verification_requests_new === 0, to: "/app/requests",                                   testId: "chk-customer-requests" },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="daily-checklist">
      <h2 className="font-display text-xl font-semibold">{t("dash.checklist.title")}</h2>
      <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">{t("dash.checklist.subtitle")}</p>
      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li key={it.label}>
            <Link to={it.to} data-testid={it.testId}
                  className="flex items-center gap-3 py-1.5 rounded-md hover:bg-slate-50 px-1">
              <span className={`w-5 h-5 rounded-full grid place-items-center flex-shrink-0 ${
                it.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
              <span className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-slate-800"}`}>
                {it.label}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Panel({ title, subtitle, items, empty, link, accent, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">{subtitle}</p>
        </div>
        {link && (
          <Link to={link.to} className="text-sm text-[#1B4332] font-medium hover:underline inline-flex items-center gap-1">
            {link.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">{empty}</div>
        )}
        {items.map((b) => (
          <Link
            key={b.id}
            to={`/app/bookings/${b.id}`}
            className="block py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg"
            data-testid={`panel-row-${b.id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{b.customer_name}</div>
                <div className="text-xs text-slate-500 font-mono">{b.booking_reference}</div>
              </div>
              <div className="flex items-center gap-2">
                {accent === "risk" && <RiskPill risk={b.risk_status} />}
                <StatusBadge status={b.status} />
              </div>
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {b.vehicle?.name || "-"} · {fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)}
            </div>
            <div className="text-xs text-slate-500">{fmtCurrency(b.price)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function BillingBanner({ billing }) {
  const status = billing.subscription_status;
  const pct = billing.fleet.unlimited
    ? 0
    : Math.round(((billing.fleet.used || 0) / Math.max(1, billing.fleet.total || 1)) * 100);
  const overFleet = billing.fleet.over_limit;
  const expired = status === "trial_expired" || status === "past_due";
  const trial = status === "trial_active";
  const grace = status === "grace_period";

  const tone = expired ? "rose" : grace || overFleet ? "amber" : "emerald";
  const bg = {
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[tone];

  return (
    <div data-testid="dashboard-billing-banner"
         className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${bg}`}>
      <div className="flex items-start gap-3">
        {expired ? <AlertOctagon className="w-5 h-5 mt-0.5" />
                  : <Crown className="w-5 h-5 mt-0.5 text-amber-500" />}
        <div>
          <div className="font-medium">
            {expired ? "Your subscription needs attention"
              : trial ? `Free trial: ${billing.trial_days_left} day(s) left on ${billing.plan.name}`
              : grace ? "Grace period: please renew to keep active features"
              : `${billing.plan.name} plan · active`}
          </div>
          <div className="text-xs mt-0.5 opacity-90">
            {billing.fleet.unlimited
              ? "Unlimited fleet capacity"
              : `Fleet: ${billing.fleet.used} / ${billing.fleet.total} pts (${pct}%) · Staff: ${billing.staff.used}${billing.staff.unlimited ? "" : ` / ${billing.staff.limit}`}`}
          </div>
        </div>
      </div>
      <Link to="/app/billing" data-testid="dashboard-billing-link"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/80 border border-current/30 hover:bg-white text-sm font-medium">
        <Receipt className="w-3.5 h-3.5" /> Manage billing →
      </Link>
    </div>
  );
}

// Phase 2J: replaces the old empty-state-only OnboardingHints. Tracks the
// exact 4-step activation funnel (spec S) explicitly, from signals already
// in `dashboard/stats`: a simple checklist, not a weighted score (that's
// what BusinessReadinessCard is for, shown below this as the *secondary*,
// ongoing prompt once activation is done).
function GettingStartedCard({ gettingStarted: g }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("confirmph_getting_started_dismissed") === "1"; }
    catch { return false; }
  });
  const steps = [
    { key: "business_created", label: "Business created", done: !!g.business_created },
    { key: "first_vehicle",    label: "Add your first vehicle", done: !!g.first_vehicle, to: "/app/vehicles", cta: "Add vehicle" },
    { key: "first_booking",    label: "Create your first real booking", done: !!g.first_booking, to: "/app/bookings/new", cta: "New booking" },
    { key: "first_confirmation", label: "Get your first customer confirmation", done: !!g.first_confirmation, to: "/app/bookings", cta: "View bookings" },
  ];
  const allDone = steps.every((s) => s.done);

  if (allDone && dismissed) return null;

  if (allDone) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-3"
           data-testid="getting-started-collapsed">
        <span className="text-sm text-emerald-900 font-medium inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> You&apos;re set up! ✓
        </span>
        <button onClick={() => {
                  setDismissed(true);
                  try { localStorage.setItem("confirmph_getting_started_dismissed", "1"); } catch {}
                }}
                data-testid="getting-started-dismiss"
                className="text-emerald-700 hover:text-emerald-900 p-1 rounded-md hover:bg-emerald-100">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="getting-started-card">
      <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Getting started</div>
      <h2 className="font-display text-xl font-semibold text-emerald-900 mt-1">
        {steps.filter((s) => s.done).length} of {steps.length} steps done
      </h2>
      <ul className="mt-4 space-y-2">
        {steps.map((s) => (
          <li key={s.key}>
            {s.done || !s.to ? (
              <div className="flex items-center gap-3 py-1.5 px-1">
                <span className={`w-5 h-5 rounded-full grid place-items-center flex-shrink-0 ${
                  s.done ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-300 border border-slate-200"}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                </span>
                <span className={`text-sm ${s.done ? "text-emerald-900" : "text-slate-600"}`}>{s.label}</span>
              </div>
            ) : (
              <Link to={s.to} data-testid={`getting-started-step-${s.key}`}
                    className="flex items-center justify-between gap-3 py-1.5 px-1 rounded-md hover:bg-emerald-100/60">
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full grid place-items-center flex-shrink-0 bg-white text-slate-300 border border-slate-200">
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </span>
                  <span className="text-sm text-slate-800">{s.label}</span>
                </div>
                <span className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1">
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function HowItWorksModal({ onClose }) {
  const steps = [
    ["1", "Customer books any way they want",
      "Messenger, phone, WhatsApp, Facebook page, walk-in: keep using whatever already works."],
    ["2", "Staff adds the booking here",
      "Vehicle, dates, price, customer details. Takes about 30 seconds."],
    ["3", "Customer receives a confirmation link",
      "They review the details and tap Confirm or Request a Change. You both have proof in writing."],
    ["4", "ConfirmPH watches for problems",
      "Double-bookings, tight buffers, and incidents are flagged before they bite."],
    ["5", "Affected customers are warned automatically",
      "If a vehicle is damaged or unavailable, we flag the bookings it might affect."],
    ["6", "Activity log keeps a clear paper trail",
      "Every status change, every message, every reminder: recorded."],
  ];
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 overflow-y-auto p-4"
         onClick={onClose} data-testid="how-it-works-modal">
      <div className="min-h-full flex items-end sm:items-center justify-center">
        <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl my-4"
             onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">In 6 steps</div>
            <h2 className="font-display text-2xl font-bold mt-1">How ConfirmPH works</h2>
          </div>
          <button onClick={onClose} data-testid="how-it-works-close"
                  className="p-1 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ol className="px-5 pb-5 space-y-4">
          {steps.map(([n, t, d]) => (
            <li key={n} className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center font-display font-semibold text-sm shrink-0">
                {n}
              </div>
              <div>
                <div className="font-medium text-slate-900">{t}</div>
                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
      </div>
    </div>
  );
}
